import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { testCaseAPI, executeAPI, proxyAPI, proxyListAPI } from '../services/api';

function ExecuteTest() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [testCase, setTestCase] = useState(null);
  const [loading, setLoading] = useState(true);
  const [executing, setExecuting] = useState(false);
  const [error, setError] = useState(null);
  const [proxies, setProxies] = useState([]);
  const [proxyLists, setProxyLists] = useState([]);
  const [proxyMode, setProxyMode] = useState('none'); // 'none', 'single', 'list'
  const [selectedProxy, setSelectedProxy] = useState('');
  const [selectedProxyList, setSelectedProxyList] = useState('');

  const [config, setConfig] = useState({
    startRange: 0,
    endRange: '',
    concurrency: 10,
    produce_outputs: false,
    use_s3: false
  });

  const [proxyStrategy, setProxyStrategy] = useState({
    max_retries: 2,
    max_requests_per_proxy: '',
    remove_on_failure: true
  });

  const [proxyParameterName, setProxyParameterName] = useState('proxy_url');

  useEffect(() => {
    fetchTestCase();
    fetchProxies();
    fetchProxyLists();
  }, [id]);

  const fetchProxies = async () => {
    try {
      const response = await proxyAPI.getAll(null, true); // Only active proxies
      setProxies(response.data);
    } catch (err) {
      console.error('Failed to fetch proxies:', err);
    }
  };

  const fetchProxyLists = async () => {
    try {
      const response = await proxyListAPI.getAll(null, true); // Only active proxy lists
      setProxyLists(response.data);
    } catch (err) {
      console.error('Failed to fetch proxy lists:', err);
    }
  };

  const fetchTestCase = async () => {
    try {
      setLoading(true);
      const response = await testCaseAPI.getById(id);
      setTestCase(response.data);

      // Use totalTests field (works for both inline and dataset-based test cases)
      const totalTests = response.data.totalTests || response.data.tests?.length || 0;

      // Set default proxy if test case has one
      if (response.data.defaultProxyId) {
        setSelectedProxy(response.data.defaultProxyId);
      }

      // Set proxy parameter name from test case
      if (response.data.proxyParameterName) {
        setProxyParameterName(response.data.proxyParameterName);
      }

      setConfig(prev => ({
        ...prev,
        endRange: totalTests
      }));
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleConfigChange = (e) => {
    const { name, value, type, checked } = e.target;
    setConfig(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const setQuickRange = (count) => {
    const total = testCase?.totalTests || testCase?.tests?.length || 0;
    const end = Math.min(count, total);
    setConfig(prev => ({
      ...prev,
      startRange: 0,
      endRange: end
    }));
  };

  const handleProxyStrategyChange = (e) => {
    const { name, value, type, checked } = e.target;
    setProxyStrategy(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleExecute = async () => {
    try {
      setExecuting(true);
      setError(null);

      const executionConfig = {
        startRange: parseInt(config.startRange),
        endRange: parseInt(config.endRange),
        concurrency: parseInt(config.concurrency),
        produce_outputs: config.produce_outputs,
        use_s3: config.use_s3,
        success_sample_rate: config.success_sample_rate || 1.0,
        failure_sample_rate: config.failure_sample_rate || 1.0
      };

      // Add proxy configuration based on mode
      if (proxyMode === 'single' && selectedProxy) {
        executionConfig.proxyId = selectedProxy;
        executionConfig.proxyParameterName = proxyParameterName;
        executionConfig.proxy_strategy = {
          max_retries: parseInt(proxyStrategy.max_retries) || 1
        };
      } else if (proxyMode === 'list' && selectedProxyList) {
        executionConfig.proxyListId = selectedProxyList;
        executionConfig.proxyParameterName = proxyParameterName;
        executionConfig.proxy_strategy = {
          max_retries: parseInt(proxyStrategy.max_retries),
          max_requests_per_proxy: proxyStrategy.max_requests_per_proxy ? parseInt(proxyStrategy.max_requests_per_proxy) : null,
          remove_on_failure: proxyStrategy.remove_on_failure
        };
      } else if (proxyMode === 'none' && proxyStrategy.max_retries && parseInt(proxyStrategy.max_retries) > 1) {
        // Add retry strategy for no-proxy mode
        executionConfig.proxy_strategy = {
          max_retries: parseInt(proxyStrategy.max_retries)
        };
      }

      const response = await executeAPI.run(id, executionConfig);

      // Navigate to result page
      navigate(`/result/${response.data.resultId}`);
    } catch (err) {
      // Handle different error types
      let errorMessage = 'Execution failed: ';

      if (err.code === 'ECONNABORTED' || err.message.includes('timeout')) {
        errorMessage += 'Request timed out after 15 minutes. Try reducing the test range or concurrency.';
      } else if (err.response?.data?.error) {
        errorMessage += err.response.data.error;
      } else if (err.response?.data?.details) {
        errorMessage += JSON.stringify(err.response.data.details);
      } else {
        errorMessage += err.message;
      }

      setError(errorMessage);
    } finally {
      setExecuting(false);
    }
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  if (error && !testCase) {
    return (
      <div className="card">
        <div className="alert alert-danger">{error}</div>
        <Link to="/" className="btn btn-secondary">Back to Test Cases</Link>
      </div>
    );
  }

  const totalTests = testCase?.totalTests || testCase?.tests?.length || 0;
  const testsToRun = Math.max(0, parseInt(config.endRange) - parseInt(config.startRange));

  return (
    <div>
      <div className="card">
        <h2>Execute Test: {testCase?.name}</h2>

        <div className="alert alert-info">
          <strong>Endpoint:</strong> {testCase?.endpoint}<br />
          <strong>Method:</strong> {testCase?.method}<br />
          <strong>Total Tests Available:</strong> {totalTests}
        </div>

        {error && (
          <div className="alert alert-danger">{error}</div>
        )}

        <h3>Execution Configuration</h3>

        <div className="mb-3">
          <label><strong>Quick Presets:</strong></label>
          <div className="flex" style={{ gap: '0.5rem', marginTop: '0.5rem' }}>
            <button
              type="button"
              onClick={() => setQuickRange(10)}
              className="btn btn-secondary"
              style={{ fontSize: '0.875rem', padding: '0.5rem 1rem' }}
            >
              First 10
            </button>
            <button
              type="button"
              onClick={() => setQuickRange(50)}
              className="btn btn-secondary"
              style={{ fontSize: '0.875rem', padding: '0.5rem 1rem' }}
            >
              First 50
            </button>
            <button
              type="button"
              onClick={() => setQuickRange(100)}
              className="btn btn-secondary"
              style={{ fontSize: '0.875rem', padding: '0.5rem 1rem' }}
            >
              First 100
            </button>
            <button
              type="button"
              onClick={() => setConfig(prev => ({ ...prev, startRange: 0, endRange: totalTests }))}
              className="btn btn-secondary"
              style={{ fontSize: '0.875rem', padding: '0.5rem 1rem' }}
            >
              All Tests
            </button>
          </div>
        </div>

        <div className="grid grid-2">
          <div className="form-group">
            <label>Start Range (Index)</label>
            <input
              type="number"
              name="startRange"
              value={config.startRange}
              onChange={handleConfigChange}
              min="0"
              max={totalTests - 1}
            />
            <small>Start from test index (0-based)</small>
          </div>

          <div className="form-group">
            <label>End Range (Index)</label>
            <input
              type="number"
              name="endRange"
              value={config.endRange}
              onChange={handleConfigChange}
              min="1"
              max={totalTests}
            />
            <small>End at test index (exclusive)</small>
          </div>

          <div className="form-group">
            <label>Concurrency</label>
            <input
              type="number"
              name="concurrency"
              value={config.concurrency}
              onChange={handleConfigChange}
              min="1"
              max="100"
            />
            <small>Number of parallel requests</small>
          </div>

          <div className="form-group">
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <input
                type="checkbox"
                name="produce_outputs"
                checked={config.produce_outputs}
                onChange={handleConfigChange}
                style={{ width: 'auto' }}
              />
              Include detailed outputs
            </label>
            <small>Save full response bodies (increases storage)</small>
          </div>

          <div className="form-group">
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <input
                type="checkbox"
                name="use_s3"
                checked={config.use_s3}
                onChange={handleConfigChange}
                style={{ width: 'auto' }}
                disabled={!config.produce_outputs}
              />
              Store results in S3
            </label>
            <small>
              {config.produce_outputs
                ? 'Use S3 storage for large results (recommended for 1000+ tests)'
                : 'Enable detailed outputs first'}
            </small>
          </div>

          {config.produce_outputs && (
            <div className="card" style={{ marginTop: '1rem', background: '#f8f9fa', padding: '1rem' }}>
              <h4 style={{ marginTop: 0 }}>Response Sampling</h4>
              <p style={{ fontSize: '0.85rem', color: '#666', marginBottom: '1rem' }}>
                Control what percentage of responses to save (reduces S3 storage)
              </p>
              <div className="grid grid-2" style={{ gap: '1rem' }}>
                <div>
                  <label>Success Sample Rate (%)</label>
                  <input
                    type="number"
                    value={(config.success_sample_rate || 1.0) * 100}
                    onChange={(e) => setConfig({
                      ...config,
                      success_sample_rate: parseFloat(e.target.value) / 100
                    })}
                    min="0"
                    max="100"
                    step="10"
                  />
                  <small>Example: 10 = save 10% of successful responses</small>
                </div>
                <div>
                  <label>Failure Sample Rate (%)</label>
                  <input
                    type="number"
                    value={(config.failure_sample_rate || 1.0) * 100}
                    onChange={(e) => setConfig({
                      ...config,
                      failure_sample_rate: parseFloat(e.target.value) / 100
                    })}
                    min="0"
                    max="100"
                    step="10"
                  />
                  <small>Example: 100 = save 100% of failed responses</small>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="form-group">
          <label>Proxy Configuration</label>
          <div className="flex" style={{ gap: '1rem', marginBottom: '1rem' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <input
                type="radio"
                name="proxyMode"
                value="none"
                checked={proxyMode === 'none'}
                onChange={(e) => setProxyMode(e.target.value)}
                style={{ width: 'auto' }}
              />
              No Proxy
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <input
                type="radio"
                name="proxyMode"
                value="single"
                checked={proxyMode === 'single'}
                onChange={(e) => setProxyMode(e.target.value)}
                style={{ width: 'auto' }}
              />
              Single Proxy
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <input
                type="radio"
                name="proxyMode"
                value="list"
                checked={proxyMode === 'list'}
                onChange={(e) => setProxyMode(e.target.value)}
                style={{ width: 'auto' }}
              />
              Proxy List (with retry)
            </label>
          </div>

          {proxyMode === 'none' && (
            <div className="mt-2">
              <label>Max Retries (for no proxy)</label>
              <input
                type="number"
                value={proxyStrategy.max_retries || 1}
                onChange={(e) => setProxyStrategy({
                  ...proxyStrategy,
                  max_retries: parseInt(e.target.value) || 1
                })}
                min="1"
                max="10"
              />
              <small>Number of retry attempts if request fails (default: 1 = no retries)</small>
            </div>
          )}

          {(proxyMode === 'single' || proxyMode === 'list') && (
            <div className="form-group" style={{ backgroundColor: '#f8f9fa', padding: '1rem', borderRadius: '4px', marginBottom: '1rem' }}>
              <label>Proxy Query Parameter Name</label>
              <input
                type="text"
                value={proxyParameterName}
                onChange={(e) => setProxyParameterName(e.target.value)}
                placeholder="e.g., proxy_url, proxy, proxy_server"
              />
              <small>
                The query parameter name to pass the proxy URL (default: proxy_url)
              </small>
            </div>
          )}

          {proxyMode === 'single' && (
            <div>
              <select
                value={selectedProxy}
                onChange={(e) => setSelectedProxy(e.target.value)}
              >
                <option value="">Select a proxy</option>
                {proxies.map(proxy => (
                  <option key={proxy._id} value={proxy._id}>
                    {proxy.name}
                  </option>
                ))}
              </select>
              <small>Single proxy will be used for all requests</small>

              {selectedProxy && (
                <div className="mt-2">
                  <label>Max Retries (for single proxy)</label>
                  <input
                    type="number"
                    value={proxyStrategy.max_retries || 1}
                    onChange={(e) => setProxyStrategy({
                      ...proxyStrategy,
                      max_retries: parseInt(e.target.value) || 1
                    })}
                    min="1"
                    max="10"
                  />
                  <small>Number of retry attempts if request fails (default: 1 = no retries)</small>
                </div>
              )}
            </div>
          )}

          {proxyMode === 'list' && (
            <div>
              <div className="form-group">
                <select
                  value={selectedProxyList}
                  onChange={(e) => setSelectedProxyList(e.target.value)}
                >
                  <option value="">Select a proxy list</option>
                  {proxyLists.map(list => (
                    <option key={list._id} value={list._id}>
                      {list.name} ({list.proxies.filter(p => p.isActive).length} active proxies)
                    </option>
                  ))}
                </select>
                <small>Proxies will rotate with retry logic</small>
              </div>

              {selectedProxyList && (
                <div className="card" style={{ padding: '1rem', backgroundColor: '#f8f9fa', marginTop: '1rem' }}>
                  <h4 style={{ marginTop: 0 }}>Proxy Strategy</h4>

                  <div className="grid grid-2">
                    <div className="form-group">
                      <label>Max Retries per Test</label>
                      <input
                        type="number"
                        name="max_retries"
                        value={proxyStrategy.max_retries}
                        onChange={handleProxyStrategyChange}
                        min="1"
                        max="10"
                      />
                      <small>Number of attempts if request fails (1-10)</small>
                    </div>

                    <div className="form-group">
                      <label>Max Requests per Proxy</label>
                      <input
                        type="number"
                        name="max_requests_per_proxy"
                        value={proxyStrategy.max_requests_per_proxy}
                        onChange={handleProxyStrategyChange}
                        min="1"
                        placeholder="Unlimited"
                      />
                      <small>Leave empty for unlimited</small>
                    </div>
                  </div>

                  <div className="form-group">
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <input
                        type="checkbox"
                        name="remove_on_failure"
                        checked={proxyStrategy.remove_on_failure}
                        onChange={handleProxyStrategyChange}
                        style={{ width: 'auto' }}
                      />
                      Remove failed proxies from rotation
                    </label>
                    <small>Stop using a proxy after first failure</small>
                  </div>
                </div>
              )}

              {proxyLists.length === 0 && (
                <div className="alert alert-info" style={{ marginTop: '0.5rem' }}>
                  No proxy lists available. <Link to="/proxy-lists/create">Create one</Link> to use this feature.
                </div>
              )}
            </div>
          )}
        </div>

        <div className="alert alert-warning">
          <strong>Tests to execute:</strong> {testsToRun} tests<br />
          <strong>Range:</strong> {config.startRange} to {config.endRange}
        </div>

        <div className="flex mt-3">
          <button
            onClick={handleExecute}
            className="btn btn-success"
            disabled={executing || testsToRun <= 0}
          >
            {executing ? 'Executing...' : 'Run Tests'}
          </button>
          <Link to={`/test-case/${id}`} className="btn btn-secondary">
            Cancel
          </Link>
        </div>

        {executing && (
          <div className="loading mt-3">
            <div className="spinner"></div>
            <p>Running tests... This may take a few minutes.</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default ExecuteTest;
